import { Link, NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRecommendations, listDomains } from '../../lib/api';
import { BETA_MODE } from '../../lib/beta';
import { DOMAIN_TABS } from '../../lib/domain-tabs';
import { useFeature } from '../../lib/useFeature';
import { Logo } from '../../components/brand/Logo';

export interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  icon: keyof typeof ICONS;
}

/** 18px stroke icons (Feather-style), inheriting `currentColor`. */
const ICONS: Record<string, ReactNode> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </>
  ),
  domains: (
    <>
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </>
  ),
  members: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  activity: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  help: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  billing: (
    <>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </>
  ),
};

function NavIcon({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      {ICONS[name]}
    </svg>
  );
}

/**
 * The application's left navigation rail (logo + primary nav). Rendered fixed on
 * desktop and inside a slide-in drawer on mobile; `onNavigate` lets the drawer
 * close itself when a link is tapped.
 */
export function Sidebar({
  items,
  onNavigate,
  className = '',
}: {
  items: NavItem[];
  onNavigate?: () => void;
  className?: string;
}) {
  const { pathname } = useLocation();
  // Only when the user is in the Domains section do we surface the list of
  // domains nested under the "Domains" item (contextual sub-nav).
  const inDomains = pathname.startsWith('/domains');
  const domains = useQuery({
    queryKey: ['domains'],
    queryFn: listDomains,
    enabled: inDomains,
  });
  // The domain you're currently inside — its sub-pages (Overview, Explorer, Cost
  // Optimization, Settings) get nested under it, mirroring the page's top tabs.
  const activeDomainId = pathname.match(/^\/domains\/([^/]+)/)?.[1];
  // Count of new (undecided) optimizations for the active domain — badges the
  // Optimizations sub-item (moved here from the old in-page tab bar).
  const recs = useQuery({
    queryKey: ['recommendations', activeDomainId],
    queryFn: () => getRecommendations(activeDomainId!),
    enabled: inDomains && !!activeDomainId,
  });
  const openCount = (recs.data?.items ?? []).filter((r) => r.status === 'open').length;
  const openBadge = openCount > 9 ? '9+' : String(openCount);
  // The Compliance tab appears only when the NIS2 lens is rolled out to the org.
  const complianceOn = useFeature('compliance');
  const domainTabs = DOMAIN_TABS.filter((tab) => tab.to !== 'compliance' || complianceOn);
  // Settings is a section (admin-only): surface its sub-pages — Organization
  // Settings and Billing — when the user is anywhere within it.
  const inSettings = pathname.startsWith('/settings');

  return (
    <aside
      className={`flex w-60 shrink-0 flex-col border-r border-hairline bg-surface ${className}`}
    >
      <div className="px-5 py-4">
        <Link to="/" aria-label="Lizrd home" onClick={onNavigate}>
          <Logo size={24} />
        </Link>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
        {items.map((item) => (
          <div key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-tour={`nav:${item.to}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-surface-2 font-medium text-bone'
                    : 'text-haze hover:bg-surface-2/60 hover:text-bone'
                }`
              }
            >
              <NavIcon name={item.icon} />
              {item.label}
            </NavLink>

            {item.to === '/domains' && inDomains && (domains.data?.length ?? 0) > 0 && (
              <div className="mt-0.5 space-y-0.5 border-l border-hairline pl-3 ml-5">
                {domains.data!.map((env) => {
                  const isActiveDomain = env.id === activeDomainId;
                  return (
                    <div key={env.id}>
                      <NavLink
                        to={`/domains/${env.id}`}
                        onClick={onNavigate}
                        title={env.name}
                        className={() =>
                          // Highlight the domain when you're anywhere inside it (any
                          // of its sub-pages), not only on its index route.
                          `block truncate rounded-md px-2.5 py-1.5 text-[0.8rem] transition-colors ${
                            isActiveDomain
                              ? 'bg-surface-2 text-bone'
                              : 'text-muted hover:bg-surface-2/50 hover:text-bone'
                          }`
                        }
                      >
                        {env.name}
                      </NavLink>

                      {/* The active domain's sub-pages, mirroring its top tabs. */}
                      {isActiveDomain && (
                        <div className="mt-0.5 space-y-0.5 border-l border-hairline pl-2.5 ml-3">
                          {domainTabs.map((tab) => (
                            <NavLink
                              key={tab.to}
                              to={`/domains/${env.id}/${tab.to}`}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                `flex items-center justify-between gap-1 rounded-md px-2.5 py-1.5 text-[0.75rem] transition-colors ${
                                  isActive
                                    ? 'bg-surface-2 text-bone'
                                    : 'text-muted hover:bg-surface-2/50 hover:text-bone'
                                }`
                              }
                            >
                              <span className="truncate">{tab.label}</span>
                              {tab.to === 'cost-optimization' && openCount > 0 && (
                                <span
                                  className="inline-flex h-4 min-w-4 flex-none items-center justify-center rounded-full bg-iris px-1 text-[0.6rem] font-bold text-on-iris"
                                  title={`${openCount} new optimization${openCount === 1 ? '' : 's'}`}
                                >
                                  {openBadge}
                                </span>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {item.to === '/settings' && inSettings && (
              <div className="mt-0.5 space-y-0.5 border-l border-hairline pl-3 ml-5">
                {[
                  { to: '/settings', label: 'Organization Settings', end: true },
                  // Billing is hidden while in private beta (nothing is charged yet).
                  ...(BETA_MODE ? [] : [{ to: '/settings/billing', label: 'Billing', end: false }]),
                ].map((sub) => (
                  <NavLink
                    key={sub.to}
                    to={sub.to}
                    end={sub.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `block truncate rounded-md px-2.5 py-1.5 text-[0.8rem] transition-colors ${
                        isActive
                          ? 'bg-surface-2 text-bone'
                          : 'text-muted hover:bg-surface-2/50 hover:text-bone'
                      }`
                    }
                  >
                    {sub.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}
